/**
 * Locating settings in the DOM.
 *
 * <Setting> stamps its identifier onto a real element, so nothing needs to register itself in a
 * React context - the DOM is the registry, which also means settings rendered into portals
 * (Panels, Dialogs, Callouts) are found without any extra plumbing.
 */

export const SETTING_ATTRIBUTE = 'data-setting-id';

const escapeAttributeValue = (value: string): string => value.replace(/["\\]/g, '\\$&');

export const settingSelector = (id: string): string => `[${SETTING_ATTRIBUTE}="${escapeAttributeValue(id)}"]`;

/**
 * Prefers a match that actually occupies space.
 *
 * A collapsed accordion renders a hidden copy of its children while a deep link is pending, so the
 * same id can be in the DOM twice: once hidden, once real. Taking the first match would sometimes
 * return the hidden one - and once the accordion expands, that node is unmounted and measuring it
 * gives zeros. When nothing is visible yet (everything still collapsed) the hidden match is returned
 * so the caller can find it and ask its container to open.
 */
export const findSettingElement = (id: string, root: ParentNode = document): HTMLElement | null => {
    const matches = Array.from(root.querySelectorAll<HTMLElement>(settingSelector(id)));
    const visible = matches.find((el) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 || rect.height > 0 || getSettingRect(el) !== undefined;
    });
    return visible ?? matches[0] ?? null;
};

/** Every setting currently rendered, in document order. */
export const findRenderedSettingIds = (root: ParentNode = document): string[] =>
    Array.from(root.querySelectorAll<HTMLElement>(`[${SETTING_ATTRIBUTE}]`))
        .map((el) => el.getAttribute(SETTING_ATTRIBUTE) ?? '')
        .filter(Boolean);

const unionRect = (rects: DOMRect[]): DOMRect | undefined => {
    const boxes = rects.filter((r) => r.width > 0 || r.height > 0);
    if (boxes.length === 0) return undefined;
    const left = Math.min(...boxes.map((r) => r.left));
    const top = Math.min(...boxes.map((r) => r.top));
    const right = Math.max(...boxes.map((r) => r.right));
    const bottom = Math.max(...boxes.map((r) => r.bottom));
    return new DOMRect(left, top, right - left, bottom - top);
};

/**
 * The rectangle to draw a highlight around.
 *
 * A `display: contents` marker generates no box of its own, so fall back to the union of whatever
 * its children occupy.
 */
export const getSettingRect = (element: HTMLElement): DOMRect | undefined => {
    const own = element.getBoundingClientRect();
    if (own.width > 0 || own.height > 0) return own;
    return unionRect(Array.from(element.children).map((child) => child.getBoundingClientRect()));
};

/**
 * Scroll a setting into view, including when it sits inside a nested scrolling container such as
 * the settings content pane. A `display: contents` marker cannot be scrolled to itself, so scroll
 * to the first child that occupies space.
 */
export const scrollSettingIntoView = (element: HTMLElement): void => {
    const own = element.getBoundingClientRect();
    const scrollTarget =
        own.width > 0 || own.height > 0
            ? element
            : Array.from(element.children).find((child) => {
                  const rect = child.getBoundingClientRect();
                  return rect.width > 0 || rect.height > 0;
              });
    scrollTarget?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
};
